import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, MapPin, User, Phone, Mail, Zap, Star, MessageCircle, FileText, Pencil, Save, XCircle } from "lucide-react";

export default function BookingDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [notes, setNotes] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});

  const load = () => api.get(`/admin/bookings/${id}`).then((r) => { setB(r.data); setNotes(r.data.admin_notes || ""); setDraft({}); });
  useEffect(() => {
    load();
    api.get("/admin/drivers?status=approved").then((r) => setDrivers(r.data));
  }, [id]); // eslint-disable-line

  if (!b) return <div className="p-10 text-slate-500">Loading…</div>;

  const doAssign = async () => {
    if (!selectedDriver) return;
    try { await api.post(`/admin/bookings/${id}/assign`, { driver_id: selectedDriver }); toast.success("Driver assigned"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doAuto = async () => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/auto-assign`);
      if (data.success) toast.success("Auto-assigned");
      else toast.warning(data.message || "No driver found");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const changeStatus = async (status) => {
    try { await api.patch(`/admin/bookings/${id}/status`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveNotes = async () => {
    try { await api.patch(`/admin/bookings/${id}/status`, { status: b.status, admin_notes: notes }); toast.success("Notes saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const startEdit = () => {
    setDraft({
      client_name: b.client_name || "",
      client_email: b.client_email || "",
      client_phone: b.client_phone || "",
      pickup_address: b.pickup_address || "",
      destination_address: b.destination_address || "",
      service_datetime: (b.service_datetime || "").slice(0, 16),
      service_duration_hours: b.service_duration_hours || 1,
      passengers: b.passengers || 1,
      luggage: b.luggage || 0,
      flight_number: b.flight_number || "",
      base_price: b.base_price || b.total_amount || 0,
      client_pays: b.client_pays || b.total_amount || 0,
      total_amount: b.total_amount || 0,
      driver_earning: b.driver_earning || 0,
    });
    setEditMode(true);
  };
  const saveEdit = async () => {
    try {
      const payload = {
        ...draft,
        service_datetime: new Date(draft.service_datetime).toISOString(),
      };
      await api.patch(`/admin/bookings/${id}`, payload);
      toast.success("Reserva actualizada");
      setEditMode(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="p-10 max-w-[1400px]">
      <Link to="/admin/bookings" className="inline-flex items-center gap-2 text-xs uppercase text-slate-500 hover:text-[#f59e0b] mb-6"><ArrowLeft className="w-3.5 h-3.5" /> All bookings</Link>

      <PageHeader eyebrow={b.type.toUpperCase()} title={<><span className="font-mono text-[#f59e0b] text-3xl mr-3">{b.reference}</span>{b.client_name}</>} description={`${b.pickup_address} → ${b.destination_address}`}>
        <StatBadge status={b.payment_status} />
        <StatBadge status={b.status} />
        {editMode ? (
          <>
            <GhostButton onClick={() => setEditMode(false)} data-testid="cancel-edit"><XCircle className="w-3.5 h-3.5" /> Cancelar</GhostButton>
            <GoldButton onClick={saveEdit} data-testid="save-edit"><Save className="w-3.5 h-3.5" /> Guardar</GoldButton>
          </>
        ) : (
          <GhostButton onClick={startEdit} data-testid="edit-booking"><Pencil className="w-3.5 h-3.5" /> Editar</GhostButton>
        )}
      </PageHeader>

      <div className="grid grid-cols-3 gap-5">
        {/* Left: main details */}
        <div className="col-span-2 space-y-5">
          <Section title="Service">
            <div className="grid grid-cols-2 gap-6">
              {editMode ? (
                <>
                  <Fld l="Date & time"><Input type="datetime-local" value={draft.service_datetime} onChange={(e) => setD("service_datetime", e.target.value)} data-testid="edit-datetime" /></Fld>
                  <Fld l="Duration (h)"><Input type="number" min="0.5" step="0.5" value={draft.service_duration_hours} onChange={(e) => setD("service_duration_hours", Number(e.target.value))} data-testid="edit-duration" /></Fld>
                  <Fld l="Passengers"><Input type="number" min="1" value={draft.passengers} onChange={(e) => setD("passengers", Number(e.target.value))} data-testid="edit-passengers" /></Fld>
                  <Fld l="Luggage"><Input type="number" min="0" value={draft.luggage} onChange={(e) => setD("luggage", Number(e.target.value))} data-testid="edit-luggage" /></Fld>
                  <Fld l="Flight"><Input value={draft.flight_number} onChange={(e) => setD("flight_number", e.target.value)} data-testid="edit-flight" /></Fld>
                  <div />
                  <Fld l="Pickup"><Input value={draft.pickup_address} onChange={(e) => setD("pickup_address", e.target.value)} data-testid="edit-pickup" /></Fld>
                  <Fld l="Destination"><Input value={draft.destination_address} onChange={(e) => setD("destination_address", e.target.value)} data-testid="edit-destination" /></Fld>
                </>
              ) : (
                <>
                  <Info l="Date & time" v={new Date(b.service_datetime).toLocaleString("en-GB", { dateStyle: "full", timeStyle: "short" })} />
                  <Info l="Duration" v={`${b.service_duration_hours}h`} />
                  <Info l="Passengers" v={`${b.passengers} pax · ${b.luggage} lug`} />
                  <Info l="Flight" v={b.flight_number || "—"} />
                  <Info l="Pickup" v={b.pickup_address} icon={MapPin} />
                  <Info l="Destination" v={b.destination_address} icon={MapPin} />
                </>
              )}
            </div>
          </Section>

          <Section title="Price breakdown">
            {editMode ? (
              <div className="grid grid-cols-2 gap-4">
                <Fld l="Base price €"><Input type="number" min="0" step="0.01" value={draft.base_price} onChange={(e) => setD("base_price", Number(e.target.value))} data-testid="edit-base-price" /></Fld>
                <Fld l="Client pays €"><Input type="number" min="0" step="0.01" value={draft.client_pays} onChange={(e) => setD("client_pays", Number(e.target.value))} data-testid="edit-client-pays" /></Fld>
                <Fld l="Total €"><Input type="number" min="0" step="0.01" value={draft.total_amount} onChange={(e) => setD("total_amount", Number(e.target.value))} data-testid="edit-total" /></Fld>
                <Fld l="Driver earning €"><Input type="number" min="0" step="0.01" value={draft.driver_earning} onChange={(e) => setD("driver_earning", Number(e.target.value))} data-testid="edit-driver-earning" /></Fld>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <Metric label="Base price" value={`€${(b.base_price ?? b.total_amount).toFixed(2)}`} />
                  <Metric label="Partner markup" value={`+€${(b.partner_markup ?? 0).toFixed(2)}`} />
                  <Metric label="Client pays" value={`€${(b.client_pays ?? b.total_amount).toFixed(2)}`} accent />
                </div>
                <div className="grid grid-cols-4 gap-3 border-t border-slate-200 pt-4">
                  <MiniMetric label="Platform fee" value={`€${b.platform_fee.toFixed(2)}`} />
                  <MiniMetric label="Fleet commission" value={`€${(b.fleet_commission || 0).toFixed(2)}`} />
                  <MiniMetric label="Driver earns" value={`€${b.driver_earning.toFixed(2)}`} />
                  <MiniMetric label="Partner earns" value={`€${(b.partner_earning ?? b.partner_fee ?? 0).toFixed(2)}`} accent />
                </div>
              </>
            )}
          </Section>

          <Section title="Split de comissões">
            <CommissionSplit b={b} onChanged={load} />
          </Section>

          <Section title="Client">
            {editMode ? (
              <div className="grid grid-cols-3 gap-4">
                <Fld l="Name"><Input value={draft.client_name} onChange={(e) => setD("client_name", e.target.value)} data-testid="edit-client-name" /></Fld>
                <Fld l="Email"><Input type="email" value={draft.client_email} onChange={(e) => setD("client_email", e.target.value)} data-testid="edit-client-email" /></Fld>
                <Fld l="Phone"><Input value={draft.client_phone} onChange={(e) => setD("client_phone", e.target.value)} data-testid="edit-client-phone" /></Fld>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                <Info l="Name" v={b.client_name} icon={User} />
                <Info l="Email" v={b.client_email} icon={Mail} />
                <Info l="Phone" v={b.client_phone} icon={Phone} />
              </div>
            )}
          </Section>

          <Section title="Driver assignment">
            {b.driver ? (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 border border-[#f59e0b] flex items-center justify-center text-[#f59e0b] font-display text-xl">{b.driver.name?.[0]}</div>
                <div className="flex-1">
                  <div className="font-display text-lg text-[#0a0f1e]">{b.driver.name}</div>
                  <div className="text-xs text-slate-500">{b.driver.phone} · {(b.driver.languages || []).join(", ")}</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="w-3 h-3 text-[#f59e0b] fill-[#f59e0b]" />
                    <span className="text-xs text-[#0a0f1e]">{b.driver.rating_avg?.toFixed(1)}</span>
                    <span className="text-xs text-slate-500">({b.driver.trips_count} trips)</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-slate-500 text-sm">No driver assigned yet.</div>
            )}
            <div className="mt-4 flex gap-3 items-center">
              <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} className="input-dt flex-1" data-testid="assign-select">
                <option value="">Select driver…</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} · ★ {d.rating_avg?.toFixed(1)}</option>)}
              </select>
              <GhostButton onClick={doAssign} disabled={!selectedDriver} data-testid="manual-assign">Assign</GhostButton>
              <GoldButton onClick={doAuto} data-testid="auto-assign"><Zap className="w-4 h-4" /> Auto-assign</GoldButton>
            </div>
          </Section>

          <Section title="Dispatch WhatsApp (Iter 23)">
            <DispatchLog bookingId={b.id} bookingStatus={b.status} onChanged={load} />
          </Section>

          <Section title="Admin notes">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="input-dt" data-testid="admin-notes" />
            <div className="flex justify-end mt-3"><GoldButton onClick={saveNotes} data-testid="save-notes">Save notes</GoldButton></div>
          </Section>
        </div>

        {/* Right: sidebar */}
        <div className="space-y-5">
          <div className="border border-[#f59e0b]/40 bg-white p-6">
            <p className="label-caps text-[#f59e0b] mb-3">Security code</p>
            <div className="font-display text-4xl text-[#0a0f1e] tracking-widest" data-testid="security-code">DT-{b.security_code}</div>
            <p className="text-xs text-slate-500 mt-2">Client must show this on pickup.</p>
          </div>

          <Section title="Payment">
            <Info l="Transaction" v={b.transaction_id || "—"} />
            <Info l="Method" v={b.payment_method || "—"} />
            <Info l="Invoice" v={b.invoice_url ? <a href={b.invoice_url} target="_blank" rel="noreferrer" className="text-[#f59e0b] hover:underline flex items-center gap-1"><FileText className="w-3 h-3" />View PDF</a> : "—"} />
          </Section>

          <Section title="Change status">
            <div className="grid grid-cols-2 gap-2">
              {["confirmed", "assigned", "in_progress", "completed", "cancelled", "refund_pending"].map((s) => (
                <button key={s} onClick={() => changeStatus(s)} data-testid={`status-${s}`} className="text-[10px] uppercase tracking-wide border border-slate-200 text-slate-500 hover:border-[#f59e0b] hover:text-[#f59e0b] py-2">
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Timeline">
            <div className="space-y-2">
              {(b.status_history || []).map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-l-2 border-[#f59e0b] pl-3">
                  <span className="text-[#0a0f1e] uppercase">{h.status.replace(/_/g, " ")}</span>
                  <span className="text-slate-500 font-mono">{new Date(h.at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="WhatsApp log">
            {(b.whatsapp_log || []).length === 0 && <div className="text-xs text-slate-500">No messages sent yet.</div>}
            {(b.whatsapp_log || []).map((m, i) => (
              <div key={i} className="border border-slate-200 p-3 mb-2">
                <div className="flex items-center gap-2 text-xs text-[#f59e0b] mb-1"><MessageCircle className="w-3 h-3" /> {m.template}</div>
                <pre className="text-[11px] text-slate-500 whitespace-pre-wrap font-mono">{m.body}</pre>
              </div>
            ))}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <p className="label-caps mb-4">{title}</p>
      {children}
    </div>
  );
}
function Info({ l, v, icon: Icon }) {
  return (
    <div>
      <div className="label-caps mb-1 flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />}{l}</div>
      <div className="text-[#0a0f1e] text-sm">{v}</div>
    </div>
  );
}
function Fld({ l, children }) {
  return (
    <div>
      <div className="label-caps mb-1.5">{l}</div>
      {children}
    </div>
  );
}
function Metric({ label, value, accent }) {
  return (
    <div className={`p-4 border ${accent ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200"}`}>
      <div className="label-caps mb-1">{label}</div>
      <div className={`font-display text-2xl ${accent ? "text-[#f59e0b]" : "text-[#0a0f1e]"}`}>{value}</div>
    </div>
  );
}
function MiniMetric({ label, value, accent }) {
  return (
    <div>
      <div className="label-caps mb-1">{label}</div>
      <div className={`font-mono text-sm ${accent ? "text-[#f59e0b] font-semibold" : "text-[#0a0f1e]"}`}>{value}</div>
    </div>
  );
}

function CommissionSplit({ b, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({
    hotel_commission_eur: b.hotel_commission_eur || 0,
    ditours_commission_eur: b.ditours_commission_eur || 0,
    driver_payout_eur: b.driver_payout_eur || 0,
  });
  useEffect(() => {
    setF({
      hotel_commission_eur: b.hotel_commission_eur || 0,
      ditours_commission_eur: b.ditours_commission_eur || 0,
      driver_payout_eur: b.driver_payout_eur || 0,
    });
  }, [b.id, b.hotel_commission_eur, b.ditours_commission_eur, b.driver_payout_eur]);

  const total = Number(b.total_amount || 0);
  const sum = Number(f.hotel_commission_eur) + Number(f.ditours_commission_eur) + Number(f.driver_payout_eur);
  const diff = Math.round((total - sum) * 100) / 100;

  const save = async () => {
    try {
      await api.patch(`/admin/bookings/${b.id}`, {
        hotel_commission_eur: Number(f.hotel_commission_eur),
        ditours_commission_eur: Number(f.ditours_commission_eur),
        driver_payout_eur: Number(f.driver_payout_eur),
      });
      toast.success("Split actualizado");
      setEdit(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const recompute = async () => {
    try {
      await api.post(`/admin/bookings/${b.id}/recompute-commission`);
      toast.success("Split recalculado (auto)");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-600">
          Total: <span className="font-mono font-bold text-[#0a0f1e]">€{total.toFixed(2)}</span>
          <span className={`ml-3 px-2 py-0.5 text-[10px] uppercase font-bold ${b.commission_source === "manual" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="split-source">
            {b.commission_source === "manual" ? "Manual" : "Auto"}
          </span>
        </div>
        <div className="flex gap-2">
          {edit ? (
            <>
              <GhostButton onClick={() => setEdit(false)} className="!h-8 !text-[10px]">Cancelar</GhostButton>
              <GoldButton onClick={save} className="!h-8 !text-[10px]" data-testid="save-split">Guardar</GoldButton>
            </>
          ) : (
            <>
              <GhostButton onClick={recompute} className="!h-8 !text-[10px]" data-testid="recompute-split">Recalcular Auto</GhostButton>
              <GhostButton onClick={() => setEdit(true)} className="!h-8 !text-[10px]" data-testid="edit-split">Editar</GhostButton>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <SplitCard label="Comissão Hotel" value={f.hotel_commission_eur} editable={edit} testId="split-hotel" onChange={(v) => setF({ ...f, hotel_commission_eur: v })} accent="hotel" total={total} />
        <SplitCard label="Comissão DiTours" value={f.ditours_commission_eur} editable={edit} testId="split-ditours" onChange={(v) => setF({ ...f, ditours_commission_eur: v })} accent="ditours" total={total} />
        <SplitCard label="Payout Motorista" value={f.driver_payout_eur} editable={edit} testId="split-driver" onChange={(v) => setF({ ...f, driver_payout_eur: v })} accent="driver" total={total} />
      </div>
      {edit && (
        <div className={`mt-3 text-xs font-mono ${Math.abs(diff) < 0.01 ? "text-emerald-700" : "text-red-700"}`} data-testid="split-diff">
          Soma: €{sum.toFixed(2)} · Diferença: €{diff.toFixed(2)} {Math.abs(diff) >= 0.01 && "⚠ deve igualar o total"}
        </div>
      )}
    </div>
  );
}

function SplitCard({ label, value, editable, onChange, testId, accent, total }) {
  const pct = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : "0.0";
  const border = accent === "hotel" ? "border-blue-300" : accent === "ditours" ? "border-[#f59e0b]" : "border-emerald-400";
  const color = accent === "hotel" ? "text-blue-700" : accent === "ditours" ? "text-[#f59e0b]" : "text-emerald-700";
  return (
    <div className={`border ${border} p-4 bg-white`} data-testid={testId}>
      <div className="label-caps mb-1">{label}</div>
      {editable ? (
        <Input type="number" step="0.01" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="input-dt !h-9" />
      ) : (
        <div className={`font-display text-2xl ${color}`}>€{Number(value || 0).toFixed(2)}</div>
      )}
      <div className="text-[10px] text-slate-500 font-mono mt-1">{pct}% do total</div>
    </div>
  );
}

function DispatchLog({ bookingId, bookingStatus, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/bookings/${bookingId}/dispatch/log`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [bookingId]);

  const trigger = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${bookingId}/dispatch`);
      toast.success("Dispatch iniciado");
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const forceNext = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${bookingId}/dispatch/next`);
      toast.success("Rotado para o próximo motorista");
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const badge = (s) => {
    const map = {
      pending: "bg-amber-100 text-amber-800",
      accepted: "bg-emerald-100 text-emerald-800",
      rejected: "bg-red-100 text-red-800",
      timeout: "bg-slate-200 text-slate-700",
    };
    return <span className={`px-2 py-0.5 text-[10px] uppercase font-bold ${map[s] || "bg-slate-100"}`}>{s}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-600">
          {items.length === 0 ? "Sem tentativas de dispatch." : `${items.length} tentativa(s)`}
        </div>
        <div className="flex gap-2">
          <Link to="/admin/settings/dispatch" className="text-[10px] uppercase tracking-widest text-slate-600 hover:text-[#f59e0b] font-bold">Configuração →</Link>
          <button
            onClick={trigger}
            disabled={busy || bookingStatus === "assigned" || bookingStatus === "completed"}
            data-testid="trigger-dispatch"
            className="text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold disabled:opacity-30"
          >
            Iniciar dispatch
          </button>
          {items.some((i) => i.status === "pending") && (
            <button
              onClick={forceNext}
              disabled={busy}
              data-testid="force-next"
              className="text-[10px] uppercase tracking-widest text-red-700 hover:underline font-bold"
            >
              Próximo →
            </button>
          )}
        </div>
      </div>
      {loading && items.length === 0 && <div className="text-slate-500 text-xs">A carregar…</div>}
      {items.length > 0 && (
        <div className="space-y-2" data-testid="dispatch-log">
          {items.map((a, i) => (
            <div key={a.id} className="flex items-start gap-3 border-l-2 border-[#f59e0b] pl-3 py-2">
              <div className="w-6 h-6 flex items-center justify-center border border-slate-200 font-mono text-[10px] font-bold text-slate-700 mt-0.5">{i + 1}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-[#0a0f1e] text-sm">{a.driver_name}</span>
                  {badge(a.status)}
                  {a.low_payout && (
                    <span className="px-2 py-0.5 text-[10px] uppercase font-bold bg-amber-100 text-amber-800" title={a.low_payout_reason}>
                      ⚠ Payout baixo
                    </span>
                  )}
                  <span className="ml-auto text-[10px] text-slate-500 font-mono">{new Date(a.sent_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}</span>
                </div>
                <div className="text-[11px] text-slate-600">
                  {a.driver_phone} · Payout €{Number(a.payout_eur || 0).toFixed(2)} · via {a.notify_channel} ({a.notify_status})
                </div>
                {a.low_payout && a.low_payout_reason && (
                  <div className="text-[10px] text-amber-700 mt-1 italic">{a.low_payout_reason}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
